import { useCallback, useEffect, useMemo, useState } from "react";
import { captureError } from "@/lib/sentry";
import { useParams, Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import AskGamlyOverlay from "@/components/AskGamlyOverlay";
import MLBGameday from "@/components/MLBGameday";
import NBAGameday from "@/components/NBAGameday";
import TeamLogo from "@/components/TeamLogo";
import { GAMLY_PERSONALITIES } from "@/lib/constants";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  BroadcastIcon,
  BaseballIcon,
  ArrowLeftIcon,
  ArrowsClockwiseIcon,
  LightningIcon,
  EyeIcon,
  ChartBarIcon,
  ClockCounterClockwiseIcon,
  BookmarkIcon,
  EyeglassesIcon,
  MicrophoneIcon,
  QuotesIcon,
  LockKeyIcon,
} from "@phosphor-icons/react";
import WorldSeriesBadge from "@/components/WorldSeriesBadge";

const CARD_META = {
  key_talking_point: { label: "Key Talking Point", icon: LightningIcon, accent: "#FDB927" },
  player_to_watch: { label: "Player To Watch", icon: EyeIcon, accent: "#0A84FF" },
  stat_of_the_night: { label: "Stat Of The Night", icon: ChartBarIcon, accent: "#32D74B" },
  historical_comparison: { label: "Historical Comparison", icon: ClockCounterClockwiseIcon, accent: "#FF9F0A" },
  storyline_to_follow: { label: "Storyline To Follow", icon: BookmarkIcon, accent: "#BF5AF2" },
  what_to_watch_next: { label: "What To Watch Next", icon: EyeglassesIcon, accent: "#5AC8FA" },
  interview_question: { label: "Interview Question", icon: MicrophoneIcon, accent: "#FF453A" },
  social_quote: { label: "Social Media Quote", icon: QuotesIcon, accent: "#FFFFFF" },
};
const CARD_ORDER = Object.keys(CARD_META);

const PRESS_BOX_PROMPTS = [
  "Give me a stat for this moment",
  "Draft a sharp interview question",
  "One-line hot take for air",
  "What should I tease next?",
  "Say that in Spanish",
];

export default function PressBoxLive() {
  const { gameId } = useParams();
  const { user } = useAuth();
  const [game, setGame] = useState(null);
  const [cards, setCards] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState("cards"); // cards | gameday (gameday for MLB only)
  const [askSignal, setAskSignal] = useState(0);

  const isLocked = user?.plan !== "press_box_pro";
  const language = user?.language_preference || "en";
  const persona = GAMLY_PERSONALITIES.find((p) => p.value === (user?.personality || "press_box_reporter"));

  const fetchData = useCallback(async () => {
    if (isLocked) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.post(`/press-box/live/${gameId}`);
      setGame(res.data.game);
      setCards(res.data.cards || {});
      setGeneratedAt(res.data.generated_at);
    } catch (err) {
      captureError("press-box live fetch failed:", err);
      setError(err?.response?.status === 404 ? "not_found" : "error");
    } finally {
      setLoading(false);
    }
  }, [gameId, isLocked]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 90 seconds while in live mode
  useEffect(() => {
    if (isLocked || !game || game.status !== "LIVE") return;
    const id = setInterval(() => fetchData(), 90_000);
    return () => clearInterval(id);
  }, [fetchData, game, isLocked]);

  const formattedAt = useMemo(() => {
    if (!generatedAt) return null;
    return new Date(generatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  }, [generatedAt]);

  if (isLocked) {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto text-center py-20" data-testid="press-box-locked">
          <LockKeyIcon weight="fill" className="text-[#FDB927] mx-auto mb-3" size={36} />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FDB927] mb-2">Press Box Pro Required</p>
          <h1 className="font-display font-bold text-3xl uppercase tracking-tighter mb-3">Built for sports media pros</h1>
          <p className="text-sm text-white/60 mb-6">Press Box Mode auto-generates 8 production-ready cards per live game in English, Spanish, or Bilingual — for $299/mo.</p>
          <Link to="/pricing?plan=press_box_pro" data-testid="locked-cta" className="inline-flex items-center gap-1 px-5 py-2 rounded-md bg-[#FDB927] hover:bg-yellow-400 text-black text-xs font-bold uppercase tracking-wider">Upgrade to Press Box Pro</Link>
        </div>
      </AppLayout>
    );
  }

  if (error === "not_found") {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto text-center py-20" data-testid="press-box-not-found">
          <h1 className="font-display font-bold text-3xl uppercase tracking-tighter mb-3">This game isn&apos;t on today&apos;s slate</h1>
          <Link to="/press-box" className="inline-flex items-center gap-1 px-5 py-2 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white text-xs font-bold uppercase tracking-wider">
            <ArrowLeftIcon /> Back to Press Box
          </Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Link to="/press-box" data-testid="back-to-press-box" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white mb-4">
        <ArrowLeftIcon size={14} /> Back to Press Box
      </Link>

      {/* View tabs — Game Day (Live Pitch View / Shot Chart) for MLB + NBA games */}
      {(game?.league === "MLB" || game?.league === "NBA") && (
        <div className="flex gap-2 mb-5" data-testid="press-box-view-tabs">
          <TabBtn active={view === "cards"} onClick={() => setView("cards")} testid="tab-press-box">
            <BroadcastIcon weight="fill" size={14} /> Press Box
          </TabBtn>
          <TabBtn active={view === "gameday"} onClick={() => setView("gameday")} testid="tab-game-day">
            <BaseballIcon weight="fill" size={14} /> Game Day
          </TabBtn>
        </div>
      )}

      {view === "gameday" && game?.league === "MLB" && (
        <MLBGameday game={game} gameId={gameId} language={language} onAskGamly={() => setAskSignal((s) => s + 1)} />
      )}

      {view === "gameday" && game?.league === "NBA" && (
        <NBAGameday game={game} gameId={gameId} language={language} onAskGamly={() => setAskSignal((s) => s + 1)} />
      )}

      {view === "cards" && (
      <>
      {/* Broadcast-style header */}
      {game && (
        <div className="mb-6 rounded-md bg-gradient-to-r from-[#1a1a1a] via-[#0d0d0d] to-[#1a1a1a] border border-[#FDB927]/25 overflow-hidden" data-testid="press-box-header">
          <div className="px-5 py-3 flex items-center justify-between border-b border-white/5">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-[#FDB927]">
              <BroadcastIcon weight="fill" size={14} /> Press Box · {game.league}
              <WorldSeriesBadge game={game} />
            </span>
            {game.status === "LIVE" ? (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#FF0000]/15 border border-[#FF453A]/40">
                <span className="live-dot" />
                <span className="text-[10px] font-black uppercase tracking-[0.15em] text-[#FF453A]">{game.period_label}</span>
              </span>
            ) : (
              <span className="text-[10px] font-black uppercase tracking-[0.15em] text-white/60" data-testid="press-box-status">{game.period_label}</span>
            )}
          </div>
          <div className="px-5 py-5 grid grid-cols-3 items-center gap-4">
            <TeamScore abbr={game.away_team_abbr} name={game.away_team} score={game.away_score} color={game.away_team_color} logo={game.away_team_logo} />
            <div className="text-center">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-1">Key Matchup</p>
              <p className="font-display font-bold text-sm uppercase tracking-tight text-white/85">{game.key_matchup}</p>
            </div>
            <TeamScore abbr={game.home_team_abbr} name={game.home_team} score={game.home_score} color={game.home_team_color} logo={game.home_team_logo} home />
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/50">
          {formattedAt ? `Updated ${formattedAt}` : "Generating…"}
          {game?.status === "LIVE" && <span className="ml-3 text-[#32D74B]">· auto-refresh 90s</span>}
        </p>
        <button
          onClick={fetchData}
          disabled={loading}
          data-testid="refresh-press-box"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/15 text-white/80 text-xs font-bold uppercase tracking-wider hover:bg-white/5 disabled:opacity-40"
        >
          <ArrowsClockwiseIcon weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* 8 cards grid */}
      {loading && !cards && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cards-loading">
          {CARD_ORDER.map((k) => (
            <div key={k} className="p-4 rounded-md bg-[#121212] border border-white/10 animate-pulse h-36">
              <div className="h-3 w-24 bg-white/10 rounded mb-3" />
              <div className="h-2 w-full bg-white/5 rounded mb-1.5" />
              <div className="h-2 w-5/6 bg-white/5 rounded mb-1.5" />
              <div className="h-2 w-3/4 bg-white/5 rounded" />
            </div>
          ))}
        </div>
      )}

      {cards && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="press-box-cards">
          {CARD_ORDER.map((key) => {
            const meta = CARD_META[key];
            const value = cards[key];
            if (!value) return null;
            return (
              <article
                key={key}
                data-testid={`card-${key}`}
                className="p-4 rounded-md bg-[#121212] border border-white/10 hover:border-white/25 transition-colors"
                style={{ borderTop: `2px solid ${meta.accent}` }}
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <meta.icon weight="bold" size={14} style={{ color: meta.accent }} />
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: meta.accent }}>{meta.label}</p>
                </div>
                <p className="text-sm text-white/90 leading-relaxed">{value}</p>
              </article>
            );
          })}
        </div>
      )}

      {error === "error" && (
        <div className="mt-6 p-4 rounded-md bg-[#FF453A]/10 border border-[#FF453A]/30 text-sm text-white/80" data-testid="press-box-error">
          Couldn&apos;t generate press box cards. Try refreshing — production AI service may be busy.
        </div>
      )}
      </>
      )}

      {/* Floating Ask Gamly — lets commentators ask live questions mid-broadcast */}
      {game && <AskGamlyOverlay gameId={gameId} language={language} persona={persona} openSignal={askSignal} suggestions={PRESS_BOX_PROMPTS} />}
    </AppLayout>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition-colors ${
        active
          ? "bg-[#FDB927] text-black"
          : "bg-[#121212] border border-white/15 text-white/70 hover:text-white hover:border-white/30"
      }`}
    >
      {children}
    </button>
  );
}

function TeamScore({ abbr, name, score, color, logo, home }) {
  return (
    <div className={`flex items-center gap-3 ${home ? "flex-row-reverse text-right" : ""}`}>
      <TeamLogo abbr={abbr} logo={logo} color={color} size={48} />
      <div className={home ? "text-right" : ""}>
        <p className="font-display font-bold uppercase text-sm tracking-tight text-white/85 max-w-[140px] truncate">{name}</p>
        <p className="font-num text-3xl font-black">{score}</p>
      </div>
    </div>
  );
}
